/**
 * Learning area (courses, lessons, quizzes, learning points, course forum):
 * small helpers shared by the explicit learning events and the activity-log
 * bridge. Video start / progress / complete / stop are NOT pushed from here —
 * they come from the bridge (README.md).
 */
import { track, type EventParams } from "./dataLayer";

/**
 * Part number of a course id: "happy-family-part-3" → 3, "effective-health" → 1.
 * Each part is sold and completed on its own, so course_part is what
 * course_part_complete and the funnel reports group by.
 */
export const coursePartOf = (courseId: unknown): number | undefined => {
    if (typeof courseId !== "string" || !courseId) return undefined;
    const m = /-part-(\d+)$/.exec(courseId);
    return m ? Number(m[1]) : 1;
};

/** Score as a coarse bucket: GA4 needs no exact score to find who struggles. */
export const scoreBucket = (score: number, total: number): string | undefined => {
    if (!(total > 0) || !Number.isFinite(score)) return undefined;
    const pct = Math.round((Math.max(0, Math.min(score, total)) / total) * 100);
    if (pct >= 100) return "100";
    if (pct >= 75) return "75-99";
    if (pct >= 50) return "50-74";
    if (pct >= 25) return "25-49";
    return "0-24";
};

/**
 * A learning event: course_id is always paired with course_part, so every
 * call site does not have to derive it.
 */
export const trackLearning = (
    event: string,
    params: EventParams & { course_id?: string },
): boolean =>
    track(event, {
        ...params,
        course_part: params.course_part ?? coursePartOf(params.course_id),
    });

// ---- Duration hints for iframe players ----
//
// Kinescope plays in an iframe: the activity log only sees positions
// (media.virtual) and its play_start / play_end carry no duration, so the
// bridge could not compute video_progress percents. The player knows the
// duration from its SDK (Loaded / DurationChange) and leaves it here, keyed
// the same way the bridge sees the media (kind + id).

const MAX_HINTS = 50;
const durationHints = new Map<string, number>();

const hintKey = (kind: unknown, id: unknown): string | null =>
    typeof id === "string" && id ? `${typeof kind === "string" ? kind : ""}|${id}` : null;

/** Remember a player's duration for the bridge. Ignores unusable values. */
export const noteMediaDuration = (
    media: { kind?: string; id?: string } | null | undefined,
    seconds: number,
): void => {
    const key = hintKey(media?.kind, media?.id);
    if (!key || !Number.isFinite(seconds) || seconds <= 0) return;
    if (!durationHints.has(key) && durationHints.size >= MAX_HINTS) {
        const oldest = durationHints.keys().next().value;
        if (oldest !== undefined) durationHints.delete(oldest);
    }
    durationHints.set(key, seconds);
};

/** Duration left by noteMediaDuration for this media, if any. */
export const mediaDurationHint = (kind: unknown, id: unknown): number | undefined => {
    const key = hintKey(kind, id);
    return key ? durationHints.get(key) : undefined;
};
